import {
  buildFemaAlertPayload,
  type IpawsMessageContent as AlertCreatorIpawsData,
} from 'services/ipaws/ipawsPayload.service';
import type {
  FemaAlertArea,
  FemaAlertInfo,
  FemaAlertPayload,
  FemaAlertScope,
  FemaAlertStatus,
  FemaEnvironment,
  FemaMessageType,
  FemaReference,
} from 'types/fema.types';
import { FEMA_EAS_ORG_CODES, VALID_FEMA_ENVIRONMENTS } from 'types/fema.types';
import {
  DEFAULT_FEMA_ENVIRONMENT,
  deliveryChannelOptions,
  getAvailableDeliveryChannelValues,
  getCategoryDisplayLabel,
  getDeliveryChannelLabels,
  WEA_PRIORITY_VALUES,
  WEA_RESPONSE_TYPE_VALUES,
} from '../components/IpawsAlertFields/config/Ipaws.config';
import { truncateCapCoordinate } from './coordinatePrecision.utils';
import {
  getIpawsAreaPolygonCoordinates,
  getIpawsAreaShapes,
  IPAWS_AREA_TYPES,
  IPAWS_LEGACY_SAME_GEOCODE_LIMIT,
  NWEM_MAXIMUM_POLYGON_POINT_COUNT,
} from './ipawsArea.utils';
import { getIpawsPolygonFindings } from './ipawsPolygon.utils';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * CAP §3.2.1 addresses: space-delimited, entries containing whitespace in double quotes.
 * The form collects one free-form field; this splits it into the list sent to the backend.
 */
export const parseCapAddresses = (addresses?: string): string[] => {
  if (!isNonEmptyString(addresses)) return [];

  const normalized = addresses.trim();
  const matches = normalized.match(/"[^"\r\n]+"|[^\s"]+/g);

  return (matches ?? [])
    .map((recipient) => recipient.trim().replace(/^"|"$/g, '').trim())
    .filter(Boolean);
};

const parseExpiresAt = (expiresAt: string | Date): Date | null => {
  const timeToCheck = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  return Number.isNaN(timeToCheck.getTime()) ? null : timeToCheck;
};

const isValidFemaCoordinate = (coordinate: { latitude?: number; longitude?: number }): boolean =>
  Number.isFinite(coordinate?.latitude) &&
  Number.isFinite(coordinate?.longitude) &&
  Number(coordinate.latitude) >= -90 &&
  Number(coordinate.latitude) <= 90 &&
  Number(coordinate.longitude) >= -180 &&
  Number(coordinate.longitude) <= 180;

const areFemaCoordinatesEqual = (
  firstCoordinate: { latitude?: number; longitude?: number } | undefined,
  secondCoordinate: { latitude?: number; longitude?: number } | undefined,
): boolean =>
  Boolean(
    firstCoordinate &&
    secondCoordinate &&
    firstCoordinate.latitude === secondCoordinate.latitude &&
    firstCoordinate.longitude === secondCoordinate.longitude,
  );

const resolveEnvironment = (environmentCandidate: unknown): FemaEnvironment =>
  VALID_FEMA_ENVIRONMENTS.includes(environmentCandidate as FemaEnvironment)
    ? (environmentCandidate as FemaEnvironment)
    : DEFAULT_FEMA_ENVIRONMENT;

/**
 * Changes the map's `{ lat, lng }` points into the coordinate names expected by the backend.
 * A sent polygon repeats its first point at the end. The extra array level allows one area to
 * contain more than one polygon.
 */
const generateFemaPolygon = (
  coordinates?: AlertCreatorMapCoordinate[],
  coordinatePrecision?: number,
) => {
  if (!coordinates) return undefined;

  // The NWEM 3-decimal rounding runs first; every coordinate is then truncated to at most 4
  // decimal places (FEMA advisory 627).
  const normalizeCoordinate = (coordinate: number) =>
    truncateCapCoordinate(
      coordinatePrecision === undefined
        ? coordinate
        : Number(coordinate.toFixed(coordinatePrecision)),
    );
  const polygon = coordinates.map(({ lat, lng }) => ({
    latitude: normalizeCoordinate(lat),
    longitude: normalizeCoordinate(lng),
  }));
  const firstCoordinate = polygon[0];
  const lastCoordinate = polygon[polygon.length - 1];

  if (
    firstCoordinate &&
    lastCoordinate &&
    (firstCoordinate.latitude !== lastCoordinate.latitude ||
      firstCoordinate.longitude !== lastCoordinate.longitude)
  ) {
    polygon.push(firstCoordinate);
  }

  return [polygon];
};

/**
 * Converts every editor shape into the fields used for one alert area.
 * Squares become four-corner polygons. Circles keep their center and radius instead of being
 * replaced by many small polygon points.
 */
export const generateFemaAreaGeometry = (
  alertData: AlertCreatorAlertData,
): Pick<FemaAlertArea, 'polygon' | 'circle'> => {
  const areaShapes = getIpawsAreaShapes(alertData) as AlertCreatorAreaShape[];
  const isNwemSelected = alertData.ipaws?.deliveryChannels?.includes('NWEM');
  const coordinatePrecision = isNwemSelected ? 3 : undefined;
  const polygons = areaShapes.flatMap((shape) => {
    if (shape.type !== IPAWS_AREA_TYPES.POLYGON && shape.type !== IPAWS_AREA_TYPES.SQUARE) {
      return [];
    }

    return generateFemaPolygon(getIpawsAreaPolygonCoordinates(shape), coordinatePrecision) ?? [];
  });
  const circles = areaShapes
    .filter((shape): shape is AlertCreatorCircleShape => shape.type === IPAWS_AREA_TYPES.CIRCLE)
    .map((shape) => ({
      center: {
        // Keep a missing center invalid so validation reports it instead of using a real location.
        latitude:
          shape.center?.lat === undefined ? Number.NaN : truncateCapCoordinate(shape.center.lat),
        longitude:
          shape.center?.lng === undefined ? Number.NaN : truncateCapCoordinate(shape.center.lng),
      },
      radius: Number(shape.radiusKm),
    }));

  // Leave unused optional fields out instead of sending empty lists.
  return {
    ...(polygons.length ? { polygon: polygons } : {}),
    ...(circles.length ? { circle: circles } : {}),
  };
};

export type {
  IpawsMessageContent as AlertCreatorIpawsData,
  IpawsMessageLanguage as AlertCreatorIpawsLanguage,
  FemaResourceInput,
  FemaValueCodeInput,
} from 'services/ipaws/ipawsPayload.service';

/** One editable map point. The map uses the short `lat` and `lng` names. */
export type AlertCreatorMapCoordinate = {
  lat: number;
  lng: number;
};

/** A hand-drawn polygon. The repeated closing point is added only when the alert is built. */
export type AlertCreatorPolygonShape = {
  id: string;
  name?: string;
  type: 'polygon';
  coordinates: AlertCreatorMapCoordinate[];
  lastInserted: AlertCreatorMapCoordinate[];
};

/** A square stored as a center and side length; its four corners are calculated when needed. */
export type AlertCreatorSquareShape = {
  id: string;
  name?: string;
  type: 'square';
  center: AlertCreatorMapCoordinate;
  sideLengthKm: number;
};

/** A circle stored as the center and radius that the backend expects. */
export type AlertCreatorCircleShape = {
  id: string;
  name?: string;
  type: 'circle';
  center: AlertCreatorMapCoordinate;
  radiusKm: number;
};

/** Every shape the IPAWS map editor can store. Optional names are never sent in CAP. */
export type AlertCreatorAreaShape =
  | AlertCreatorPolygonShape
  | AlertCreatorSquareShape
  | AlertCreatorCircleShape;

export type AlertCreatorAlertData = {
  headline?: string;
  description?: string;
  expirationTime?: string;
  issuedTime?: string;
  onsetTime?: string;
  messageType?: FemaMessageType;
  status?: FemaAlertStatus;
  scope?: FemaAlertScope;
  restriction?: string;
  /** Free-form field; split into the backend's address list when the alert is built. */
  addresses?: string;
  references?: FemaReference[];
  /** Current map shapes. Squares are sent as polygons; circles are sent as center plus radius. */
  areaShapes?: AlertCreatorAreaShape[];
  ipaws?: AlertCreatorIpawsData;
  destinationDetails?: {
    destinationName?: string;
    environmentLabel?: string;
    cogDisplayId?: string;
    issuingAuthority?: string | null;
    cogId?: string | null;
    cogName?: string | null;
    credentialStatus?: string;
    dissemination?: string;
  };
};

export type IpawsPreviewModel = {
  destination: 'IPAWS-OPEN';
  destinationName: string;
  environment: FemaEnvironment;
  environmentLabel: string;
  cogDisplayId: string;
  issuingAuthority: string;
  cogId: string;
  cogName: string;
  credentialStatus: string;
  dissemination: string;
  event: string;
  eventCode: string;
  deliveryChannels: string[];
  categories: string[];
  areaDescription: string;
  headline: string;
  description: string;
  web: string;
  contact: string;
  expiresAtLabel: string;
};

const getSameGeocodeCount = (info: FemaAlertInfo): number =>
  (info.area ?? [])
    .flatMap((area) => area.geocode ?? [])
    .filter(({ valueName, value }) => valueName === 'SAME' && isNonEmptyString(value)).length;

/** Language blocks may translate areaDesc, but their actual targeting must stay identical. */
const getAreaTargetingSignature = (info: FemaAlertInfo): string =>
  JSON.stringify(
    (info.area ?? []).map((area) => ({
      polygon: area.polygon ?? [],
      circle: area.circle ?? [],
      geocode: area.geocode ?? [],
    })),
  );

const BLOCK_CHANNEL_PARAMETER_NAME = 'BLOCKCHANNEL';
const DELIVERY_CHANNEL_VALUES = deliveryChannelOptions.map(({ value }) => value);

export { buildIpawsDeliveryChannelParameters } from 'services/ipaws/ipawsPayload.service';

/** Adapts the Alert Creator's map and form values to the shared CAP builder. */
export const buildIpawsSubmitPayload = (alertData: AlertCreatorAlertData): FemaAlertPayload =>
  buildFemaAlertPayload({
    status: alertData?.status,
    messageType: alertData?.messageType,
    scope: alertData?.scope,
    restriction: alertData?.restriction,
    addresses: parseCapAddresses(alertData?.addresses),
    references: alertData?.references,
    issuedTime: alertData?.issuedTime,
    onsetTime: alertData?.onsetTime,
    expirationTime: alertData?.expirationTime,
    headline: alertData?.headline,
    description: alertData?.description,
    content: alertData?.ipaws,
    areaGeometry: generateFemaAreaGeometry(alertData),
  });

/**
 * Checks the final alert object before sending it.
 * These checks give the operator quick feedback; the backend still repeats its own checks because
 * data sent over the network cannot be trusted only because the browser approved it.
 */
export const validateIpawsSubmissionPayload = (
  payload: FemaAlertPayload,
  { allowDeferredArea = false }: { allowDeferredArea?: boolean } = {},
): string[] => {
  const issues: string[] = [];

  if (!payload.info?.length) {
    return ['At least one FEMA language block is required.'];
  }

  if (payload.scope === 'Private' && !payload.addresses?.length) {
    issues.push('At least one recipient address is required for a Private scope alert.');
  }

  const primaryAreaTargetingSignature = getAreaTargetingSignature(payload.info[0]);

  payload.info.forEach((info, index) => {
    const blockLabel = index === 0 ? '' : ` for language block ${index + 1}`;

    if (!isNonEmptyString(info.language) && index > 0) {
      issues.push(`Language is required${blockLabel}.`);
    }

    if (!info.category?.length) {
      issues.push(`At least one category is required${blockLabel}.`);
    }

    if (!isNonEmptyString(info.event)) {
      issues.push(`Event is required${blockLabel}.`);
    }

    const blockedChannels = new Set(
      (info.parameter ?? [])
        .filter(({ valueName }) => valueName === BLOCK_CHANNEL_PARAMETER_NAME)
        .map(({ value }) => value),
    );
    const selectedDeliveryChannels = DELIVERY_CHANNEL_VALUES.filter(
      (channel) => !blockedChannels.has(channel),
    );
    if (!selectedDeliveryChannels.length) {
      issues.push(`At least one delivery channel is required${blockLabel}.`);
    } else {
      const sameEventCode =
        info.eventCode?.find(({ valueName }) => valueName === 'SAME')?.value ?? '';
      const availableDeliveryChannels = new Set(getAvailableDeliveryChannelValues(sameEventCode));
      if (selectedDeliveryChannels.some((channel) => !availableDeliveryChannels.has(channel))) {
        issues.push(
          `The selected delivery channels are not supported by the SAME event code${blockLabel}.`,
        );
      }
    }

    if (selectedDeliveryChannels.includes('CMAS')) {
      if (
        info.responseType?.some(
          (responseType) => !WEA_RESPONSE_TYPE_VALUES.some((value) => value === responseType),
        )
      ) {
        issues.push(
          `Recommended Public Action contains a value that is not supported by WEA${blockLabel}.`,
        );
      }

      const weaPriorityFields = [
        ['urgency', info.urgency],
        ['severity', info.severity],
        ['certainty', info.certainty],
      ] as const;

      weaPriorityFields.forEach(([fieldPath, value]) => {
        if (!(WEA_PRIORITY_VALUES[fieldPath] as readonly string[]).includes(value)) {
          const label = `${fieldPath[0].toUpperCase()}${fieldPath.slice(1)}`;
          issues.push(
            `${label} must be ${WEA_PRIORITY_VALUES[fieldPath].join(' or ')} when WEA is selected${blockLabel}.`,
          );
        }
      });
    }

    if (selectedDeliveryChannels.includes('EAS') || selectedDeliveryChannels.includes('NWEM')) {
      const easOrganizationCodes = (info.parameter ?? [])
        .filter(({ valueName }) => valueName === 'EAS-ORG')
        .map(({ value }) => value);
      // Decision D18: the value is the COG profile's code, written once by the payload builder.
      // FEMA answers 402/502 when it is missing; the backend refuses a mismatch (312).
      if (
        easOrganizationCodes.length !== 1 ||
        !FEMA_EAS_ORG_CODES.some((code) => code === easOrganizationCodes[0])
      ) {
        issues.push(
          `EAS-ORG must appear once, with the COG profile's EAS organization code, for EAS or NWEM${blockLabel}.`,
        );
      }
      if (getSameGeocodeCount(info) > IPAWS_LEGACY_SAME_GEOCODE_LIMIT) {
        issues.push(
          `Use no more than ${IPAWS_LEGACY_SAME_GEOCODE_LIMIT} SAME geocodes for EAS or NWEM${blockLabel}.`,
        );
      }
    }

    if (index > 0 && getAreaTargetingSignature(info) !== primaryAreaTargetingSignature) {
      issues.push(`Area geometry and geocodes must match the primary language${blockLabel}.`);
    }

    if (!isNonEmptyString(info.headline)) {
      issues.push(`Headline is required${blockLabel}.`);
    }

    if (!isNonEmptyString(info.description)) {
      issues.push(`Description is required${blockLabel}.`);
    }

    if (!info.eventCode?.length) {
      issues.push(`Event code is required${blockLabel}.`);
    }

    if (!info.expires || (typeof info.expires === 'string' && !info.expires.trim())) {
      issues.push(`Expiration time is required${blockLabel}.`);
    }

    if (!info.area?.length && !allowDeferredArea) {
      issues.push(`Affected area description is required${blockLabel}.`);
    } else if (info.area?.length) {
      info.area.forEach((area, areaIndex) => {
        if (!isNonEmptyString(area.areaDesc)) {
          const areaLabel = info.area && info.area.length > 1 ? ` in area ${areaIndex + 1}` : '';
          issues.push(`Affected area description is required${areaLabel}${blockLabel}.`);
        }
      });
    }

    // Check the exact polygons and circles produced for the outgoing alert.
    (info.area ?? []).forEach((area, areaIndex) => {
      const areaLabel = info.area && info.area.length > 1 ? ` in area ${areaIndex + 1}` : '';

      (area.polygon ?? []).forEach((polygon, polygonIndex) => {
        const polygonLabel = `Polygon ${polygonIndex + 1}${areaLabel}${blockLabel}`;
        if (
          selectedDeliveryChannels.includes('NWEM') &&
          polygon.length > NWEM_MAXIMUM_POLYGON_POINT_COUNT
        ) {
          issues.push(
            `${polygonLabel} must contain no more than ${NWEM_MAXIMUM_POLYGON_POINT_COUNT} CAP coordinate pairs for NWEM.`,
          );
        }
        if (
          selectedDeliveryChannels.includes('NWEM') &&
          polygon.some(
            ({ latitude, longitude }) =>
              Number(latitude.toFixed(3)) !== latitude ||
              Number(longitude.toFixed(3)) !== longitude,
          )
        ) {
          issues.push(`${polygonLabel} must use no more than three decimal places for NWEM.`);
        }
        if (polygon.length < 4) {
          issues.push(`${polygonLabel} must contain at least four CAP coordinate pairs.`);
        }
        if (!polygon.every(isValidFemaCoordinate)) {
          issues.push(`${polygonLabel} must use valid WGS 84 latitude and longitude values.`);
        }
        if (!areFemaCoordinatesEqual(polygon[0], polygon.at(-1))) {
          issues.push(`${polygonLabel} must repeat its first coordinate once at the end.`);
        }
        if (polygon.length >= 4 && polygon.every(isValidFemaCoordinate)) {
          // Reuse the map checks for crossed lines, repeated points, and flat shapes.
          getIpawsPolygonFindings(
            polygon.map(({ latitude, longitude }) => ({
              lat: latitude,
              lng: longitude,
            })),
          ).forEach((finding) => issues.push(`${polygonLabel}: ${finding}`));
        }
      });

      (area.circle ?? []).forEach((circle, circleIndex) => {
        const circleLabel = `Circle ${circleIndex + 1}${areaLabel}${blockLabel}`;
        if (!isValidFemaCoordinate(circle.center)) {
          issues.push(`${circleLabel} must use a valid WGS 84 center point.`);
        }
        if (!Number.isFinite(Number(circle.radius)) || Number(circle.radius) <= 0) {
          issues.push(`${circleLabel} must have a radius greater than 0 kilometers.`);
        }
      });
    });

    const expiresAt = info.expires;

    if (!isNonEmptyString(expiresAt) && !(expiresAt instanceof Date)) {
      issues.push(`Expiration time is required${blockLabel}.`);
    } else {
      const expirationDate = parseExpiresAt(expiresAt);

      if (!expirationDate) {
        issues.push(`Expiration time must be a valid date-time${blockLabel}.`);
      } else if (expirationDate.getTime() <= Date.now()) {
        issues.push(`Expiration time must be in the future${blockLabel}.`);
      } else if (
        selectedDeliveryChannels.includes('EAS') &&
        expirationDate.getTime() >
          Math.max(Date.now(), parseExpiresAt(info.effective ?? '')?.getTime() ?? 0) +
            99.5 * 60 * 60 * 1000
      ) {
        issues.push(
          `EAS expiration must be no more than 99.5 hours after the send time${blockLabel}.`,
        );
      }
    }
  });

  return issues;
};

/** Builds the small, readable set of details shown in the confirmation dialog. */
export const buildIpawsPreviewModel = (alertData: AlertCreatorAlertData): IpawsPreviewModel => {
  const ipawsData = alertData?.ipaws ?? {};
  const destinationDetails = alertData?.destinationDetails ?? {};
  const expiresAt = alertData?.expirationTime ? new Date(alertData.expirationTime) : null;

  return {
    destination: 'IPAWS-OPEN',
    destinationName: destinationDetails.destinationName ?? 'FEMA IPAWS public warning destination',
    environment: resolveEnvironment(ipawsData.environment),
    environmentLabel:
      destinationDetails.environmentLabel ?? resolveEnvironment(ipawsData.environment),
    cogDisplayId: destinationDetails.cogDisplayId ?? 'Not provided',
    issuingAuthority: destinationDetails.issuingAuthority ?? '',
    cogId: destinationDetails.cogId ?? '',
    cogName: destinationDetails.cogName ?? '',
    credentialStatus: destinationDetails.credentialStatus ?? 'Unknown',
    dissemination: destinationDetails.dissemination ?? '',
    event: ipawsData.event ?? '',
    eventCode: ipawsData.eventCode ?? '',
    deliveryChannels: getDeliveryChannelLabels(ipawsData.deliveryChannels),
    categories: (ipawsData.category ?? []).map(getCategoryDisplayLabel),
    areaDescription: ipawsData.areaDescription ?? '',
    headline: alertData?.headline ?? '',
    description: alertData?.description ?? '',
    web: ipawsData.web ?? '',
    contact: ipawsData.contact ?? '',
    expiresAtLabel:
      expiresAt && !Number.isNaN(expiresAt.getTime()) ? expiresAt.toLocaleString() : 'Not provided',
  };
};
